import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Transaction } from "../../interfaces/Transaction";
import { getPatientTransactions } from "../../apis/patientTransaction";
import { getPatient } from "../../apis/patient";
import Modal from "../../components/Modal";
import AddPatientTransaction from "./AddPatientTransaction";
import { format } from "date-fns";
import { Pagination, PaginationProps } from "antd";

const PatientTransactionList: React.FC<{
  patientId?: number;
  onClose?: () => void;
}> = ({ patientId: propPatientId, onClose }) => {
  const { patientId: urlPatientId } = useParams<{ patientId?: string }>();
  const navigate = useNavigate();
  const patientId =
    propPatientId || (urlPatientId ? Number(urlPatientId) : undefined);
  const [editableId, setEditableId] = useState<any>(null);
  const [isTransactionModalOpen, setTransactionModalVisibility] =
    useState<boolean>(false);
  const [transactions, setTransactions] = useState<Array<Transaction>>([]);
  const [meta, setMeta] = useState<any>();
  const [patientName, setPatientName] = useState<string>("");

  const fetchTransactions = useCallback(
    (params = {}) => {
      getPatientTransactions(params).then((res: any) => {
        setTransactions(res.data.data || res.data || []);
        setMeta(res.meta);
      });
    },
    [patientId]
  );

  const fetchPatientInfo = (id: number) => {
    getPatient(id)
      .then((res: any) => {
        const patient = res.data.data || res.data;
        setPatientName(
          `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
        );
      })
      .catch((err) => {
        console.error("Failed to fetch patient info:", err);
      });
  };

  useEffect(() => {
    const params: any = { per_page: 10 };
    if (patientId) {
      params.patient_id = patientId;
      fetchPatientInfo(patientId);
    }
    fetchTransactions(params);
  }, [patientId, fetchTransactions]);

  const handleCloseModal = () => {
    setEditableId(null);
    setTransactionModalVisibility(false);
    const params: any = { per_page: 10 };
    if (patientId) {
      params.patient_id = patientId;
    }
    fetchTransactions(params);
  };

  const handleEditTransaction = (payload: any) => {
    setEditableId(payload.id);
    setTransactionModalVisibility(true);
  };

  const onPaginationChange: PaginationProps["onChange"] = (page, pageSize) => {
    const params: any = { page, per_page: pageSize };
    if (patientId) {
      params.patient_id = patientId;
    }
    fetchTransactions(params);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          {patientId && (
            <button
              onClick={() => navigate(-1)}
              className="mb-2 text-sm text-indigo-600 hover:text-indigo-900"
            >
              ← Back
            </button>
          )}
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {patientId
              ? patientName
                ? `Transactions - ${patientName}`
                : "Patient Transactions"
              : "All Patient Transactions"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {patientId
              ? "Transactions for this patient"
              : "List of all patient transactions"}
          </p>
        </div>
        <div className="mx-2 mt-4 sm:mt-0 sm:ml-16 sm:flex">
          <button
            type="button"
            onClick={() => setTransactionModalVisibility(true)}
            className="block px-3 py-2 text-sm font-semibold text-center text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Create New
          </button>
        </div>
      </div>
      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Date
                    </th>
                    {!patientId && (
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Patient
                      </th>
                    )}
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Payment Mode
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Payment Note
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions && transactions.length > 0 ? (
                    transactions.map((transaction, index) => (
                      <tr key={transaction.id || index}>
                        <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6">
                          {transaction.date &&
                            format(new Date(transaction.date), "dd MMM yyyy")}
                        </td>
                        {!patientId && (
                          <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {transaction.patient
                              ? `${transaction.patient.firstName || ""} ${
                                  transaction.patient.lastName || ""
                                }`.trim()
                              : "--"}
                          </td>
                        )}
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          ₹{transaction.amount}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 capitalize whitespace-nowrap">
                          {transaction.paymentMode}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {transaction.note ? transaction.note : "--"}
                        </td>
                        <td className="relative py-4 pr-4 pl-3 text-sm font-medium text-left whitespace-nowrap sm:pr-6">
                          <button
                            onClick={() => handleEditTransaction(transaction)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="px-4 py-2 bg-white">
                      <td
                        colSpan={patientId ? 5 : 6}
                        className="py-4 pr-3 pl-4 text-sm font-medium text-center text-gray-900 sm:pl-6"
                      >
                        No transactions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {meta && meta.total > 0 && (
              <div className="flex justify-end pt-5">
                <Pagination
                  defaultCurrent={1}
                  defaultPageSize={10}
                  total={meta.total}
                  onChange={onPaginationChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {isTransactionModalOpen && (
        <Modal
          title={
            editableId
              ? "Update Patient Transaction"
              : "Add Patient Transaction"
          }
          onClose={() => handleCloseModal()}
          modalContent={
            <AddPatientTransaction
              editableId={editableId}
              preSelectedPatientId={patientId}
              closeModal={() => handleCloseModal()}
            />
          }
        />
      )}
    </div>
  );
};

export default PatientTransactionList;
